import React from 'react';
import { Link } from 'react-router-dom';
import { RenderBlocks } from '@plone/volto/components';
import { flattenToAppURL } from '@plone/volto/helpers';
import MetadataTable from './MetadataTable';
import DocumentTabs from './DocumentTabs';
import VersionRelations from './VersionRelations';
import AutoReferences from './AutoReferences';
import FullTextSection from './FullTextSection';
import DocumentSEO from '../SEO/DocumentSEO';
import { gaEvent } from '../../helpers/analytics';
import { contactHref } from '../ContactForm/categories';

/**
 * Shared detail view for every archive document type, matching the legacy
 * portal layout (TSPType/TypesView across the three sites): status banner,
 * "docid, title" heading, Functional areas line, description, grey file
 * band, then metadata table (left) beside the Related/History/… tab panel
 * (right). Per-type differences come only from the backend document_display
 * / document_relations expanders.
 *
 * Markup follows the design system's CONTRACT F (`.prc-kit__doc`) — see
 * /tmp/ds-ref/PORT-SPEC.md on the VM. Styling comes entirely from the
 * global ds-core.css / ds-volto.css layers; this component owns data
 * wiring only.
 */

function statusInfo(content) {
  const s = content.status;
  if (!s) return null;
  const title = s.title || s.token || (typeof s === 'string' ? s : null);
  if (!title) return null;
  return { title, key: statusKey(title) };
}

/** Classify a human status label into the CONTRACT A pill/banner key set. */
function statusKey(title) {
  const t = String(title).toLowerCase();
  if (/current|active|approved|in effect/.test(t)) return 'current';
  if (/archive|archived|superseded|expired|inactive|historical/.test(t)) {
    return 'archived';
  }
  if (/cancelled|canceled/.test(t)) return 'cancelled';
  if (/rescinded|revoked/.test(t)) return 'rescinded';
  if (/private/.test(t)) return 'private';
  if (/draft/.test(t)) return 'draft';
  if (/pending|in review|proposed/.test(t)) return 'pending';
  return 'current';
}

// legacy convertBytes: two decimals, KB/MB, no space ("491.69KB")
function convertBytes(bytes) {
  if (!bytes) return '';
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(2)}MB` : `${kb.toFixed(2)}KB`;
}

// Matches plone.namedfile.browser.ALLOWED_INLINE_MIMETYPES exactly -- these
// are the only types @@display-file will actually serve inline; anything
// else falls back to attachment disposition even if linked, so there is no
// point offering a "View" link for them.
const INLINE_VIEWABLE_MIMETYPES = new Set([
  'image/gif',
  'image/jp2',
  'image/jpeg',
  'image/jpeg2000-image',
  'image/jpeg2000',
  'image/jpx',
  'image/png',
  'image/webp',
  'image/x-icon',
  'image/x-jpeg2000-image',
  'text/plain',
  'application/pdf',
]);

/** Body blocks minus the title/description blocks the view renders itself
 * (migrated documents carry an authored title block — rendering it again
 * duplicated the heading). */
function bodyContent(content) {
  const ids = (content.blocks_layout?.items || []).filter((id) => {
    const t = content.blocks?.[id]?.['@type'];
    return t !== 'title' && t !== 'description';
  });
  if (ids.length === 0) return null;
  return { ...content, blocks_layout: { ...content.blocks_layout, items: ids } };
}

const DocumentView = ({ content }) => {
  const status = statusInfo(content);
  const body = bodyContent(content);
  const file = content.file && content.file.download ? content.file : null;
  // @@display-file is stock plone.namedfile (Content-Disposition: inline for
  // allowed mimetypes, otherwise it degrades to attachment on its own) --
  // already wired into downloadCanonical.js's SEO middleware, just never
  // surfaced as a UI link until now.
  const canView =
    !!file && INLINE_VIEWABLE_MIMETYPES.has(file['content-type']);
  const viewUrl = file
    ? file.download.replace('/@@download/', '/@@display-file/')
    : null;
  const subjects = content.subjects || [];
  const heading = content.docid
    ? `${content.docid}, ${content.title}`
    : content.title;

  return (
    <div className="doc-view">
      <DocumentSEO content={content} />

      <div className="prc-kit__main">
        <article className="prc-kit__doc">
          {status ? (
            <div className="prc-kit__docbanner">
              <div className={`prc-statusbanner prc-statusbanner--${status.key}`}>
                <div className="prc-statusbanner__inner">{status.title}</div>
              </div>
            </div>
          ) : null}

          <h1 className="prc-kit__doc-h1">{heading}</h1>

          {subjects.length > 0 ? (
            <p className="prc-kit__fa">
              <b>Functional areas:</b> {subjects.join(', ')}
            </p>
          ) : null}

          {content.description ? (
            <div className="prc-kit__desc">{content.description}</div>
          ) : null}

          {file ? (
            <div className="prc-kit__download">
              <span className="prc-kit__download-ic" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M6 2.75h8.5L19 7.25V21a.75.75 0 0 1-.75.75H6a.75.75 0 0 1-.75-.75V3.5A.75.75 0 0 1 6 2.75Z" />
                  <path d="M14.5 2.75V7a.75.75 0 0 0 .75.75H19" />
                  <path d="M8.5 13h7M8.5 16.5h7M8.5 9.5h3" />
                </svg>
              </span>
              <div className="prc-kit__download-info">
                <span className="prc-kit__download-name">
                  {file.filename || 'Download'}
                </span>
                <span className="prc-kit__download-meta">
                  {convertBytes(file.size)}
                </span>
              </div>
              <div className="prc-kit__download-actions">
                {canView ? (
                  <a
                    href={flattenToAppURL(viewUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="prc-kit__download-btn prc-kit__download-btn--view"
                    onClick={() =>
                      gaEvent('file_view', {
                        file_name: file.filename || '',
                        link_url: flattenToAppURL(viewUrl),
                        docid: content.docid || '',
                      })
                    }
                  >
                    View
                  </a>
                ) : null}
                <a
                  href={flattenToAppURL(file.download)}
                  rel="noopener noreferrer"
                  className="prc-kit__download-btn"
                  onClick={() =>
                    // The bare @@download/file URL has no extension, so GA4's
                    // automatic file_download never fires for it.
                    gaEvent('file_download', {
                      file_name: file.filename || '',
                      link_url: flattenToAppURL(file.download),
                      docid: content.docid || '',
                    })
                  }
                >
                  Download
                </a>
              </div>
            </div>
          ) : null}

          {body ? (
            <div className="doc-body">
              <RenderBlocks content={body} />
            </div>
          ) : null}

          <div className="prc-kit__doc-cols">
            <div className="prc-kit__doc-meta">
              <MetadataTable content={content} />
            </div>
            <div className="prc-kit__doc-refs">
              <DocumentTabs content={content} />
            </div>
          </div>

          <VersionRelations content={content} />

          {/* Beside the curated relations, not inside them, and server-rendered
              for the same reason they are: the auto-detected glossary
              backlinks are only worth having if a crawler can see them.
              Renders nothing until the reconciler has populated the field. */}
          <AutoReferences content={content} />

          {/* Last in the article, below the metadata table: the extracted
              text is long, and putting it above the relations would push
              every navigational link off the first screen. Renders nothing
              until the reconciler has populated the field. */}
          <FullTextSection content={content} />

          {/* BL-0096. The archive's accuracy is checkable only by the people
              reading it, and this is the one place a reader has the document
              in front of them — so the link carries the document with it
              (docid + path prefill the form) rather than dropping them on a
              blank contact page to describe which record they meant. Last in
              the article, after the text, because it is an exit rather than
              part of the record. */}
          <p className="prc-kit__doc-report">
            <Link
              rel="nofollow"
              to={contactHref({
                topic: 'document-error',
                docid: content.docid || '',
                path: flattenToAppURL(content['@id'] || ''),
              })}
            >
              Something wrong with this record? Tell us
            </Link>
          </p>
        </article>
      </div>
    </div>
  );
};

export default DocumentView;
